import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Eye, KeyRound, Lock } from 'lucide-react';
import { getAuthHeader } from '../services/tokenService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/webhook/stripe/admin/reprise-mandat`;
const CAT = { deja_engagee: 'Déjà engagée', prelevee_des_la_reprise: 'Prélevée dès la reprise', a_venir: 'À venir' };

function Echeances({ plan }) {
  return (
    <table className="w-full text-xs border rounded-lg" data-testid="reprise-echeances">
      <tbody>
        {plan.echeances.map((e) => (
          <tr key={e.index} className="border-b last:border-0">
            <td className="p-1.5">n° {e.index}</td><td className="p-1.5">{e.date ? String(e.date).slice(0, 10).split('-').reverse().join('/') : '—'}</td>
            <td className="p-1.5">{e.montant.toFixed(2)} €</td><td className="p-1.5">{e.statut}</td>
            <td className="p-1.5 font-medium">{CAT[e.categorie] || e.categorie}</td><td className="p-1.5 font-mono">{e.paiement || ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function StripeRepriseMandat() {
  const [sid, setSid] = useState('');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const appel = async (method, url, params) => {
    setBusy(true);
    try {
      const r = await axios({ method, url, params, headers: getAuthHeader() });
      setRes(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Opération impossible'); }
    setBusy(false);
  };
  const bloquer = async () => {
    if (!window.confirm("Bloquer les prélèvements de ce seul plan ? Aucune opération déjà engagée n'est annulée.")) return;
    setBusy(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/sepa-stripe/admin/plans/${sid}/recovery-hold`, null, { params: { actif: true, motif: 'blocage_conservatoire_verification_stripe' }, headers: getAuthHeader() });
      toast.success('Blocage conservatoire enregistré');
    } catch (e) { toast.error(e.response?.data?.detail || 'Blocage impossible'); }
    setBusy(false);
    appel('get', `${API}/${sid}/apercu`);
  };
  const plan = res?.apres || res?.avant || (res?.echeances && res);
  const appliquer = () => window.confirm('Récupérer uniquement le mandat ? Aucun paiement ne sera créé et le blocage reste actif.') && appel('post', `${API}/${sid}`, { appliquer: true });
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="stripe-reprise-mandat">
      <div className="flex flex-wrap items-end gap-2">
        <div className="mr-auto">
          <p className="font-semibold text-slate-800">Récupération d'un mandat SEPA (sans paiement)</p>
          <p className="text-xs text-slate-500">Le blocage des prélèvements est conservé. Aucun paiement n'est créé ni confirmé.</p>
        </div>
        <input value={sid} onChange={(e) => setSid(e.target.value.trim())} placeholder="N° de souscription" className="border rounded-lg px-2 py-1 text-sm w-80" data-testid="reprise-souscription-input" />
        <button type="button" disabled={busy || !sid} onClick={() => appel('get', `${API}/${sid}/apercu`)} className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm" data-testid="reprise-apercu-btn"><Eye className="h-4 w-4" /> Aperçu</button>
        <button type="button" disabled={busy || !sid} onClick={() => appel('post', `${API}/${sid}`, { appliquer: false })} className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm" data-testid="reprise-controle-btn"><ShieldCheck className="h-4 w-4" /> Contrôler</button>
        <button type="button" disabled={busy || !res?.concordant} onClick={appliquer} className="inline-flex items-center gap-1 rounded-lg bg-orange-600 text-white px-3 py-1.5 text-sm disabled:opacity-40" data-testid="reprise-appliquer-btn"><KeyRound className="h-4 w-4" /> Récupérer le mandat</button>
        <button type="button" disabled={busy || !sid} onClick={bloquer} className="inline-flex items-center gap-1 rounded-lg bg-red-600 text-white px-3 py-1.5 text-sm disabled:opacity-40" data-testid="reprise-bloquer-btn"><Lock className="h-4 w-4" /> Bloquer les prélèvements</button>
      </div>
      {res && (
        <div className="space-y-2 text-sm text-slate-700" data-testid="reprise-resultat">
          {res.concordance && <p>Événement {res.evenement} du {new Date(res.cree_le).toLocaleString('fr-FR')} — {res.concordant ? 'références concordantes' : `écarts : ${Object.entries(res.concordance).filter(([, v]) => !v).map(([k]) => k).join(', ')}`}</p>}
          {res.mode === 'application' && <p data-testid="reprise-bilan">1er traitement : {res.premier_traitement} · 2e traitement : {res.second_traitement} · paiements créés : {res.paiements_crees} · échéances inchangées : {res.echeances_inchangees ? 'oui' : 'NON'}</p>}
          {plan && <p>Mandat : {plan.mandate_status} {plan.mandat || ''}{plan.plan_status ? ` · plan : ${plan.plan_status}` : ''} · prélèvements {plan.stripe_recovery_hold ? 'bloqués' : 'ACTIFS'}</p>}
          {plan && <Echeances plan={plan} />}
        </div>
      )}
    </div>
  );
}
